import { inject, Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import type { ID } from '@core/ngrx-legacy';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  InviteMemberPayload,
  ManageSeatsResponse,
  MemberDataUpdate,
  TeamMembersPaginationParams,
  TellentMember,
  TellentMemberResponse,
  TellentMembersResponse,
  TellentOrgAbilities,
  APP_GUID,
} from '@core/tellent-types';

import type { ManageSeatsPayload, TransferOwnershipPayload } from '../types';

@Injectable({ providedIn: 'root' })
export class TellentMembersHttpClient {
  private appGuid = inject(APP_GUID);
  constructor(protected httpApi: TellentHttpApiService) {}

  public inviteTellentMember(payload: InviteMemberPayload): Observable<TellentMember[]> {
    return this.httpApi
      .post('/members/add', null, payload)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public inviteAppMember(payload: InviteMemberPayload): Observable<TellentMember[]> {
    return this.httpApi
      .post(`/api/app/${this.appGuid}/members/add`, null, payload, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public transferOwnership({
    memberGuid,
    code,
  }: TransferOwnershipPayload): Observable<TellentMember> {
    return this.httpApi.patch(`/members/${memberGuid}/transfer-ownership`, null, { code });
  }

  public fetchMembersByGuid(guids: string[]): Observable<TellentMember[]> {
    return this.httpApi
      .get('/members', { guids }, null)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get('/members', { email }, null)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchAppMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get(`/api/app/${this.appGuid}/members`, { email }, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMemberByGuid(guid: ID | string): Observable<TellentMemberResponse> {
    return this.httpApi.get(`/members/${guid}`);
  }

  public fetchMembers(params: TeamMembersPaginationParams): Observable<TellentMembersResponse> {
    const { page, limit, q, statuses, sortBy, sortOrder, guids } = params;
    return this.httpApi.get('/members', {
      page,
      limit,
      q,
      statuses,
      sort_by: sortBy,
      sort_dir: sortOrder,
      ...(guids ? { guids } : {}),
    });
  }

  public deactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/deactivate`);
  }

  public deleteMember(payload: { guid: string }): Observable<{ success: true }> {
    return this.httpApi.delete(`/members/${payload.guid}/remove`);
  }

  public reactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/reactivate`);
  }

  public sendInvitation(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.post(`/members/${payload.guid}/invite`);
  }
  public manageSeats(payload: ManageSeatsPayload): Observable<ManageSeatsResponse> {
    return this.httpApi.post(`/seats/manage`, null, payload);
  }

  public updateMemberData(payload: MemberDataUpdate): Observable<TellentMemberResponse> {
    const formData = new FormData();

    Object.entries(payload).forEach(
      ([key, value]: [string, string | boolean | File | undefined]) => {
        // eslint-disable-next-line eqeqeq
        if (value == undefined) return;
        if (key === 'guid') return;

        const isFile = value instanceof File;

        formData.append(`${key}`, isFile ? <File>value : `${value}`);
      },
    );

    return this.httpApi.patch(`/members/${payload.guid}/update`, null, formData);
  }

  public sendPasswordResetInstructions(userGuid: string): Observable<void> {
    return this.httpApi.post(`/users/${userGuid}/reset-password`);
  }

  public updateAbilities(
    userGuid: string,
    abilities: TellentOrgAbilities[],
  ): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${userGuid}/update-abilities`, null, {
      abilities: [...abilities],
    });
  }

  public addGroups(payload: {
    memberGuid: string;
    groupsGuids: string[];
  }): Observable<TellentMemberResponse> {
    return this.manageGroups(payload.memberGuid, payload.groupsGuids, 'assign');
  }

  public removeGroups(payload: {
    memberGuid: string;
    groupsGuids: string[];
  }): Observable<TellentMemberResponse> {
    return this.manageGroups(payload.memberGuid, payload.groupsGuids, 'unassign');
  }

  private manageGroups(
    memberGuid: string,
    groupsGuids: string[],
    action: 'assign' | 'unassign',
  ): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${memberGuid}/${action}`, null, {
      teamsGuids: groupsGuids,
    });
  }
}
